import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { registerCustomer } from "../api/api";
import CountryCodeSelect from "../components/CountryCodeSelect";
import { DEFAULT_COUNTRY_CODE, combineMobile, isValidLocalNumber } from "../utils/countryCodes";

const EMPTY_FORM = {
  fullName: "",
  countryCode: DEFAULT_COUNTRY_CODE,
  mobile: "",
  email: "",
  password: "",
  confirmPassword: "",
  plotNumber: "",
  note: "",
};

function CustomerRegistration() {
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.fullName.trim() || !form.mobile.trim() || !form.email.trim() || !form.password || !form.plotNumber.trim()) {
      setError("Please fill in your name, mobile number, email, password, and farm land number.");
      return;
    }
    if (!isValidLocalNumber(form.countryCode, form.mobile)) {
      setError(
        form.countryCode === DEFAULT_COUNTRY_CODE
          ? "Please enter a 10-digit mobile number."
          : "Please enter a valid mobile number."
      );
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Please re-enter the same password in both fields.");
      return;
    }

    setSubmitting(true);
    try {
      await registerCustomer({
        full_name: form.fullName.trim(),
        mobile: combineMobile(form.countryCode, form.mobile),
        email: form.email.trim(),
        password: form.password,
        claimed_plot_number: form.plotNumber.trim(),
        note: form.note.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        setError(detail || "A registration for this mobile number is already pending review.");
      } else {
        setError(detail || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="customer-registration-page">
        <div className="registration-form-card" style={{ textAlign: "center" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>✅</div>
          <h2>Registration submitted</h2>
          <p style={{ color: "#666" }}>
            We've received your details for farm land {form.plotNumber.trim()}. Our team will verify this
            against our records and notify you once your account is approved. You won't be able to log in
            until then.
          </p>
          <button type="button" className="register-btn" onClick={() => navigate("/login")}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-registration-page">

      <div className="registration-form-card">

        <button
          type="button"
          onClick={() => navigate("/login")}
          style={{ background: "none", border: "none", padding: 0, marginBottom: "12px", color: "#2e7d32", fontWeight: 600, cursor: "pointer" }}
        >
          ← Back to Login
        </button>

        <h2>Create your account</h2>
        <p style={{ color: "#666", marginTop: "-8px" }}>
          Tell us who you are and which farm land is yours — our team will verify it and activate your account.
        </p>

        {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}

        <form onSubmit={handleSubmit}>

          <label>Full Name</label>
          <input
            type="text"
            name="fullName"
            value={form.fullName}
            onChange={handleChange}
            placeholder="Your full name"
          />

          <label>Mobile Number</label>
          <div className="phone-row">
            <CountryCodeSelect
              value={form.countryCode}
              onChange={(code) => setForm((prev) => ({ ...prev, countryCode: code }))}
            />
            <input
              type="tel"
              name="mobile"
              value={form.mobile}
              onChange={handleChange}
              placeholder={form.countryCode === DEFAULT_COUNTRY_CODE ? "9XXXXXXXXX" : "Mobile number"}
              maxLength={form.countryCode === DEFAULT_COUNTRY_CODE ? 10 : 14}
            />
          </div>

          <label>Email</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="you@example.com"
          />

          <label>Farm Land Number</label>
          <input
            type="text"
            name="plotNumber"
            value={form.plotNumber}
            onChange={handleChange}
            placeholder="e.g. JF-102"
          />

          <label>Password</label>
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            placeholder="At least 8 characters"
          />

          <label>Confirm Password</label>
          <input
            type="password"
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={handleChange}
            placeholder="Re-enter password"
          />

          <label>Note (Optional)</label>
          <textarea
            rows="4"
            name="note"
            value={form.note}
            onChange={handleChange}
            placeholder="Anything that helps us verify you, e.g. recently purchased resale"
          />

          <button type="submit" className="register-btn" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit Registration"}
          </button>

        </form>

      </div>

    </div>
  );
}

export default CustomerRegistration;
